import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Save, User, Lock, Calendar, Heart, ShieldCheck, Clock, Upload, Camera, CheckCircle2, Image as ImageIcon, Trash2, Eye } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import PasswordInput from '../components/PasswordInput';
import { validateDocument } from '../lib/documents';
import { passwordPolicyError } from '../lib/password';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientProfile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, loading: authLoading, refreshUser } = useAuth();
  const [formData, setFormData] = useState({ name: '', phone_whatsapp: '', document_id: '' });
  const [documentImageFront, setDocumentImageFront] = useState('');
  const [documentImageBack, setDocumentImageBack] = useState('');
  const [uploadingFront, setUploadingFront] = useState(false);
  const [uploadingBack, setUploadingBack] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [upgrading, setUpgrading] = useState(false);
  const [pwd, setPwd] = useState({ current_password: '', new_password: '', confirm: '' });
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdMsg, setPwdMsg] = useState(null);

  const handleUploadPhoto = async (e, side) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isFront = side === 'front';
    if (isFront) setUploadingFront(true);
    else setUploadingBack(true);
    setUploadError(null);
    try {
      const data = new FormData();
      data.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/upload/image`, {
        method: 'POST',
        credentials: 'include',
        body: data,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'Error al subir la imagen');
      if (isFront) {
        setDocumentImageFront(json.url);
      } else {
        setDocumentImageBack(json.url);
      }
    } catch (err) {
      setUploadError(err.message);
    } finally {
      if (isFront) setUploadingFront(false);
      else setUploadingBack(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwdMsg(null);
    const pwdError = passwordPolicyError(pwd.new_password);
    if (pwdError) {
      setPwdMsg({ type: 'error', text: t(pwdError) });
      return;
    }
    if (pwd.new_password !== pwd.confirm) {
      setPwdMsg({ type: 'error', text: t('auth.errorPasswordsMatch') });
      return;
    }
    setPwdSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ current_password: pwd.current_password, new_password: pwd.new_password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || t('clientProfile.errorToast'));
      setPwdMsg({ type: 'success', text: t('clientProfile.passwordChanged') });
      setPwd({ current_password: '', new_password: '', confirm: '' });
    } catch (err) {
      setPwdMsg({ type: 'error', text: err.message });
    } finally {
      setPwdSaving(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    setFormData({
      name: user.name || '',
      phone_whatsapp: user.phone_whatsapp || '',
      document_id: user.document_id || '',
    });
    setDocumentImageFront(user.document_image_front || '');
    setDocumentImageBack(user.document_image_back || '');
  }, [user, authLoading, navigate]);

  const documentCheck = validateDocument(formData.document_id);

  const handleSave = async (e) => {
    e.preventDefault();
    if (formData.document_id && !documentCheck.valid) {
      setError(t('checkout.invalidDocument'));
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/me`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: formData.name,
          phone_whatsapp: formData.phone_whatsapp,
          document_id: formData.document_id,
          document_image_front: documentImageFront,
          document_image_back: documentImageBack,
        })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Error');
      }
      await refreshUser();   // refleja los datos guardados en toda la app
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(t('clientProfile.errorToast'));
    } finally {
      setSaving(false);
    }
  };

  if (authLoading) return <div className="min-h-screen flex items-center justify-center"><div className="w-12 h-12 border-4 border-[#134168] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {(success || error) && (
        <div className={`fixed top-4 right-4 z-50 px-5 py-3 rounded-2xl shadow-lg text-white font-medium text-sm ${success ? 'bg-green-600' : 'bg-red-600'}`}>
          {success ? t('clientProfile.savedToast') : `${error}`}
        </div>
      )}

      <div className="container mx-auto px-4 max-w-xl py-8 pb-24 md:pb-8">
        <button
          onClick={() => navigate('/client/dashboard')}
          className="mb-6 flex items-center gap-2 text-gray-600 hover:text-[#134168] font-medium transition min-h-[44px]"
        >
          <ArrowLeft className="w-5 h-5" /> {t('clientProfile.backToDashboard')}
        </button>

        {/* Accesos rápidos */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <button
            onClick={() => navigate('/client/dashboard')}
            className="flex items-center gap-2 bg-white border border-gray-200 rounded-2xl p-4 hover:border-[#134168] hover:shadow-sm transition text-left"
          >
            <span className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center shrink-0"><Calendar className="w-5 h-5 text-[#134168]" /></span>
            <span className="font-semibold text-sm text-gray-800">{t('clientProfile.myBookings', 'Mis reservas')}</span>
          </button>
          <button
            onClick={() => navigate('/client/favorites')}
            className="flex items-center gap-2 bg-white border border-gray-200 rounded-2xl p-4 hover:border-[#134168] hover:shadow-sm transition text-left"
          >
            <span className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center shrink-0"><Heart className="w-5 h-5 text-red-500" /></span>
            <span className="font-semibold text-sm text-gray-800">{t('clientProfile.favorites', 'Favoritos')}</span>
          </button>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-sm p-8"
        >
          <div className="flex flex-col items-center mb-8">
            <div className="w-20 h-20 bg-[#134168] rounded-full flex items-center justify-center text-white text-3xl font-bold mb-3">
              {user?.name?.[0] || <User className="w-8 h-8" />}
            </div>
            <h1 className="text-2xl font-bold text-[#111827]">{t('clientProfile.title')}</h1>
            <p className="text-sm text-gray-500">{user?.email}</p>
            {/* Estado de verificación de correo e identidad */}
            <div className="flex gap-2 flex-wrap justify-center mt-2">
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                user?.email_verified ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
              }`}>
                {user?.email_verified
                  ? <><ShieldCheck className="w-3.5 h-3.5" /> {t('clientProfile.emailVerified', 'Correo verificado')}</>
                  : <><Clock className="w-3.5 h-3.5" /> {t('clientProfile.pendingEmailVerification', 'Confirmar correo')}</>}
              </span>
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                user?.verified ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
              }`}>
                {user?.verified
                  ? <><ShieldCheck className="w-3.5 h-3.5" /> {t('clientProfile.verified', 'Identidad verificada (KYC)')}</>
                  : <><Clock className="w-3.5 h-3.5" /> {t('clientProfile.pendingVerification', 'Identidad pendiente')}</>}
              </span>
            </div>
          </div>

          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-xl text-sm text-center">
              {t('clientProfile.savedToast')}
            </div>
          )}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm text-center">
              {error}
            </div>
          )}

          <form onSubmit={handleSave} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('clientProfile.fullName')}</label>
              <input
                type="text"
                value={formData.name}
                onChange={e => setFormData({...formData, name: e.target.value})}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168]"
                placeholder={t('clientProfile.namePlaceholderProfile')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('clientProfile.whatsappPhone')}</label>
              <input
                type="tel"
                value={formData.phone_whatsapp}
                onChange={e => setFormData({...formData, phone_whatsapp: e.target.value})}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168]"
                placeholder="+1-809-555-0100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('clientProfile.idDocument')}</label>
              <input
                type="text"
                value={formData.document_id}
                onChange={e => setFormData({...formData, document_id: e.target.value})}
                aria-invalid={!!formData.document_id && !documentCheck.valid}
                className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 ${
                  formData.document_id && !documentCheck.valid
                    ? 'border-red-300 focus:ring-red-400'
                    : 'border-gray-200 focus:ring-[#134168]'
                }`}
                placeholder="001-1234567-8"
              />
              {formData.document_id && !documentCheck.valid && (
                <p className="mt-1.5 text-xs text-red-600">{t('checkout.invalidDocument')}</p>
              )}
            </div>

            {/* Fotos de Cédula o Licencia de Conducir (KYC) */}
            <div className="pt-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('clientProfile.kycPhotosTitle', 'Fotos del Documento / Licencia (KYC)')}
              </label>
              <p className="text-xs text-gray-500 mb-3">
                {t('clientProfile.kycPhotosDesc', 'Sube una foto clara del anverso (frente) y reverso (atrás) de tu documento para verificar tu identidad y acelerar tus alquileres.')}
              </p>

              {uploadError && (
                <p className="text-xs text-red-600 mb-2">{uploadError}</p>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Frente */}
                <div className="border-2 border-dashed border-gray-200 rounded-2xl p-4 flex flex-col items-center justify-center text-center relative bg-gray-50/50 hover:bg-gray-50 transition-colors min-h-[140px]">
                  {documentImageFront ? (
                    <div className="w-full relative group">
                      <img
                        src={documentImageFront}
                        alt="Anverso"
                        className="w-full h-32 object-cover rounded-xl border border-gray-200"
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center gap-2">
                        <a
                          href={documentImageFront}
                          target="_blank"
                          rel="noreferrer"
                          className="p-2 bg-white/90 text-gray-800 rounded-full hover:bg-white shadow"
                          title="Ver imagen"
                        >
                          <Eye className="w-4 h-4" />
                        </a>
                        <button
                          type="button"
                          onClick={() => setDocumentImageFront('')}
                          className="p-2 bg-red-600 text-white rounded-full hover:bg-red-700 shadow"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <span className="text-xs font-semibold text-green-700 flex items-center justify-center gap-1 mt-2">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Frente cargado
                      </span>
                    </div>
                  ) : (
                    <label className="cursor-pointer flex flex-col items-center justify-center w-full h-full py-4">
                      <Camera className="w-8 h-8 text-[#134168] mb-1.5" />
                      <span className="text-xs font-bold text-[#134168]">
                        {uploadingFront ? t('common.uploading', 'Subiendo...') : t('clientProfile.uploadFront', 'Subir Frente (Anverso)')}
                      </span>
                      <span className="text-[11px] text-gray-400 mt-0.5">JPG, PNG o WebP</span>
                      <input
                        type="file"
                        accept="image/*"
                        disabled={uploadingFront}
                        onChange={(e) => handleUploadPhoto(e, 'front')}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>

                {/* Reverso */}
                <div className="border-2 border-dashed border-gray-200 rounded-2xl p-4 flex flex-col items-center justify-center text-center relative bg-gray-50/50 hover:bg-gray-50 transition-colors min-h-[140px]">
                  {documentImageBack ? (
                    <div className="w-full relative group">
                      <img
                        src={documentImageBack}
                        alt="Reverso"
                        className="w-full h-32 object-cover rounded-xl border border-gray-200"
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center gap-2">
                        <a
                          href={documentImageBack}
                          target="_blank"
                          rel="noreferrer"
                          className="p-2 bg-white/90 text-gray-800 rounded-full hover:bg-white shadow"
                          title="Ver imagen"
                        >
                          <Eye className="w-4 h-4" />
                        </a>
                        <button
                          type="button"
                          onClick={() => setDocumentImageBack('')}
                          className="p-2 bg-red-600 text-white rounded-full hover:bg-red-700 shadow"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <span className="text-xs font-semibold text-green-700 flex items-center justify-center gap-1 mt-2">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Reverso cargado
                      </span>
                    </div>
                  ) : (
                    <label className="cursor-pointer flex flex-col items-center justify-center w-full h-full py-4">
                      <Camera className="w-8 h-8 text-[#134168] mb-1.5" />
                      <span className="text-xs font-bold text-[#134168]">
                        {uploadingBack ? t('common.uploading', 'Subiendo...') : t('clientProfile.uploadBack', 'Subir Reverso (Atrás)')}
                      </span>
                      <span className="text-[11px] text-gray-400 mt-0.5">JPG, PNG o WebP</span>
                      <input
                        type="file"
                        accept="image/*"
                        disabled={uploadingBack}
                        onChange={(e) => handleUploadPhoto(e, 'back')}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('clientProfile.email')}</label>
              <input
                type="email"
                value={user?.email || ''}
                disabled
                className="w-full px-4 py-3 border border-gray-100 rounded-xl bg-gray-50 text-gray-400 cursor-not-allowed"
              />
              <p className="text-xs text-gray-400 mt-1">{t('clientProfile.emailDisabled')}</p>
            </div>

            <button
              type="submit"
              disabled={saving}
              className="w-full bg-[#134168] text-white py-4 rounded-full font-bold text-lg hover:bg-[#1a5490] transition-all disabled:opacity-50 flex items-center justify-center gap-2 min-h-[56px]"
            >
              <Save className="w-5 h-5" />
              {saving ? t('common.saving') : t('clientProfile.saveChanges')}
            </button>
          </form>
        </motion.div>

        {/* Cambiar contraseña */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="mt-6 bg-white rounded-3xl shadow-sm p-8"
        >
          <div className="flex items-center gap-2 mb-4">
            <Lock className="w-5 h-5 text-[#134168]" />
            <h2 className="text-lg font-bold text-[#111827]">{t('auth.changePassword')}</h2>
          </div>

          {pwdMsg && (
            <div className={`mb-4 p-3 rounded-xl text-sm text-center ${pwdMsg.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
              {pwdMsg.text}
            </div>
          )}

          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('clientProfile.currentPassword')}</label>
              <PasswordInput
                required value={pwd.current_password}
                onChange={e => setPwd({ ...pwd, current_password: e.target.value })}
                className="px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('clientProfile.newPassword')}</label>
              <PasswordInput
                required value={pwd.new_password}
                onChange={e => setPwd({ ...pwd, new_password: e.target.value })}
                placeholder={t('auth.min8Chars')}
                className="px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.confirmPassword')}</label>
              <PasswordInput
                required value={pwd.confirm}
                onChange={e => setPwd({ ...pwd, confirm: e.target.value })}
                placeholder={t('auth.repeatPassword')}
                className="px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168]"
              />
            </div>
            <button
              type="submit"
              disabled={pwdSaving}
              className="w-full bg-[#134168] text-white py-3 rounded-full font-bold hover:bg-[#1a5490] transition-all disabled:opacity-50 flex items-center justify-center gap-2 min-h-[48px]"
            >
              <Lock className="w-4 h-4" />
              {pwdSaving ? t('common.saving') : t('auth.changePassword')}
            </button>
          </form>
        </motion.div>

        {/* Upgrade a proveedor */}
        {user?.role === 'client' && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.15 }}
            className="mt-6 rounded-3xl p-6 text-white bg-gradient-to-r from-[#36C0C7] to-[#134168] shadow-lg"
          >
            <h2 className="font-heading font-bold text-xl mb-1">
              {t('clientProfile.becomeProviderTitle', '¿Tienes vehículos para alquilar?')}
            </h2>
            <p className="text-sm text-white/85 mb-4">
              {t('clientProfile.becomeProviderDesc', 'Convierte tu cuenta en proveedor, verifica tu identidad y empieza a generar ingresos con tu flota.')}
            </p>
            <button
              type="button"
              disabled={upgrading}
              onClick={async () => {
                setUpgrading(true);
                try {
                  const res = await fetch(`${BACKEND_URL}/api/client/become-provider`, { method: 'POST', credentials: 'include' });
                  if (res.ok) {
                    window.location.assign('/provider/verificacion');
                  } else {
                    setUpgrading(false);
                  }
                } catch { setUpgrading(false); }
              }}
              className="px-6 py-3 rounded-full bg-white text-[#134168] font-bold hover:scale-105 transition-transform disabled:opacity-60"
            >
              {upgrading ? t('common.saving', 'Procesando…') : t('clientProfile.becomeProviderCta', 'Quiero ser proveedor')}
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
